import { useQuery } from '@tanstack/react-query';

import { useMemo } from 'react';

import { ParameterMetadata } from '../../../api/api';
import { FlowRefModel } from '../Models/DomainServiceModel';
import { AGENT_TOOL_INPUT_CATEGORY, FlowDefinitionModel } from '../Models/FlowModel';
import { ActivityNode } from '../Models/GraphModel';
import { SchemaValueBindingModel } from '../Models/SchemaValueModel';
import { getAgentToolInputParameters } from '../Services/AgentToolInputParameters';
import { resolveFlowSchemaValue } from '../Services/FlowSchemaValues';
import useFlowActivityMetadata from './Metadata/useFlowActivityMetadata';
import useFlowQueries from './useFlowQueries';

export type AgentToolInputContract =
	| { status: 'loading' }
	| { status: 'error' | 'unavailable'; message: string }
	| { status: 'ready'; fields: ParameterMetadata[] };

export default function useAgentToolInputContract(activity: ActivityNode, flow: FlowDefinitionModel): AgentToolInputContract {
	const metadata = useFlowActivityMetadata(activity.data.Type);
	const queries = useFlowQueries();
	const isSubFlow = activity.data.Type === 'Agent.Tool.SubFlow';
	const subFlowRef = isSubFlow ? (activity.data.Flow as FlowRefModel | undefined) : undefined;
	const subFlow = useQuery(queries.queryFlowById(subFlowRef));
	return useMemo(() => {
		if (isSubFlow && subFlow.isError) return { status: 'error', message: 'Could not load the tool input contract. Existing bindings are preserved.' };
		if (isSubFlow && subFlowRef?.Id && !subFlow.data && subFlow.isFetching) return { status: 'loading' };
		try {
			let owner = flow;
			let binding = (activity.data.PromptSchema ?? activity.data.InputSchema) as SchemaValueBindingModel | undefined;
			if (isSubFlow) {
				if (!subFlow.data?.definition) return { status: 'unavailable', message: 'Select a Flow with an object input schema. Existing bindings are preserved.' };
				owner = JSON.parse(subFlow.data.definition) as FlowDefinitionModel;
				binding = owner.InputSchemaValue;
			}
			const schema = resolveFlowSchemaValue(owner, binding)?.Value;
			if (binding && !schema) return { status: 'error', message: 'The tool input schema reference could not be resolved. Existing bindings are preserved.' };
			if (schema) {
				if (!schema.properties && schema.type !== 'object')
					return { status: 'unavailable', message: 'The tool needs an object input schema with root properties. Existing bindings are preserved.' };
				return { status: 'ready', fields: getAgentToolInputParameters(schema) };
			}
			if (isSubFlow) return { status: 'unavailable', message: 'This Flow has no object input schema. Existing bindings are preserved.' };
			if (!metadata) return { status: 'loading' };
			return { status: 'ready', fields: metadata.parameters.filter((field) => field.categories?.includes(AGENT_TOOL_INPUT_CATEGORY)) };
		} catch {
			return { status: 'error', message: 'Could not load the tool input contract. Existing bindings are preserved.' };
		}
	}, [activity.data.PromptSchema, activity.data.InputSchema, flow, isSubFlow, subFlowRef?.Id, subFlow.data, subFlow.isFetching, subFlow.isError, metadata]);
}
